import React, { ReactElement, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { useForm } from '@tanstack/react-form'
import { sortBy } from 'ramda'
import { z } from 'zod'
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchAllInstructorSessions,
  selectFOAMfratInstructors,
  selectInstructorSessions,
} from 'src/v3/content-pipeline/instructor.slice'
import {
  fetchEvents,
  selectEvents,
  selectEventsStatus,
} from 'src/v3/content-pipeline/event-pipeline.slice'
import { modalInputSx, modalPaperProps } from 'src/v3/utilities/modal-styles'
import TimeZoneDropdown from 'src/v3/components/form-fields/time-zone-dropdown'
import axios, { pluckData } from 'src/utilities/http-client'
import {
  getZodValidationError,
  shouldShowZodFieldError,
} from 'src/v3/utilities/validation'
import { useNotifications } from 'src/v3/hooks/use-notifications'

dayjs.extend(utc)
dayjs.extend(timezone)

type SessionFormModalProps = {
  open: boolean
  onClose: () => void
  instructorID: string
  initialDate?: dayjs.Dayjs | null
  editSession?: {
    id: string
    eventID: string
    name?: string
    sessionTime: string
    endTime?: string
    timeZone: string
    instructorIDs: string[]
  } | null
  isComplianceAdmin?: boolean
  // When provided alongside editSession, shows a Delete button that hands the
  // session ID back to the parent (which owns the confirmation dialog)
  onDelete?: (sessionID: string) => void
}

export default function SessionFormModal({
  open,
  onClose,
  instructorID,
  initialDate,
  editSession,
  isComplianceAdmin,
  onDelete,
}: SessionFormModalProps): ReactElement {
  let dispatch = useAppDispatch()
  let events = useAppSelector(selectEvents)
  let eventsStatus = useAppSelector(selectEventsStatus)
  let allInstructors = useAppSelector(selectFOAMfratInstructors)
  let sessions = useAppSelector(selectInstructorSessions)
  let { showError, showSuccess, NotificationComponent } = useNotifications()
  let [courseFilter, setCourseFilter] = useState<'their' | 'all'>('their')

  // How many times each currently-selected instructor has previously taught
  // each event — the strongest signal we have for "suggest a course," since
  // it reflects actual teaching history rather than a static assignment list.
  let getEventTeachCounts = (instructorIDs: string[]) => {
    let counts = new Map<string, number>()
    sessions.forEach((session) => {
      if (session.instructorIDs.some((id) => instructorIDs.includes(id))) {
        counts.set(session.eventID, (counts.get(session.eventID) ?? 0) + 1)
      }
    })
    return counts
  }

  useEffect(() => {
    if (eventsStatus === 'uninitialized') {
      dispatch(fetchEvents())
    }
  }, [eventsStatus, dispatch])

  let validationSchema = z
    .object({
      eventID: z.string().min(1, 'Event is required'),
      name: z.string(),
      sessionTime: z.string().min(1, 'Start time is required'),
      endTime: z.string(),
      timeZone: z.string().min(1, 'Timezone is required'),
      instructorIDs: z
        .array(z.string())
        .min(1, 'At least one instructor is required'),
    })
    .refine(
      (data) =>
        !data.endTime ||
        !data.sessionTime ||
        dayjs(data.endTime).isAfter(dayjs(data.sessionTime)),
      {
        message: 'End time must be after the start time',
        path: ['endTime'],
      },
    )

  let defaultInstructorIDs =
    editSession?.instructorIDs || (instructorID ? [instructorID] : [])

  let form = useForm({
    defaultValues: {
      eventID: editSession?.eventID || '',
      name: editSession?.name || '',
      sessionTime: editSession
        ? dayjs
            .utc(editSession.sessionTime)
            .tz(editSession.timeZone)
            .format('YYYY-MM-DD HH:mm:ss')
        : initialDate
          ? initialDate
              .hour(10)
              .minute(0)
              .second(0)
              .format('YYYY-MM-DD HH:mm:ss')
          : '',
      endTime: editSession?.endTime
        ? dayjs
            .utc(editSession.endTime)
            .tz(editSession.timeZone)
            .format('YYYY-MM-DD HH:mm:ss')
        : '',
      timeZone: editSession?.timeZone || dayjs.tz.guess(),
      instructorIDs: defaultInstructorIDs,
    },
    validators: {
      onChange: validationSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        let body = {
          ...(editSession && { id: editSession.id }),
          eventID: value.eventID,
          ...(value.name && { name: value.name }),
          sessionTime: dayjs
            .tz(value.sessionTime, value.timeZone)
            .utc()
            .format(),
          ...(value.endTime && {
            endTime: dayjs.tz(value.endTime, value.timeZone).utc().format(),
          }),
          timeZone: value.timeZone,
          instructorIDs: value.instructorIDs,
          userIDs: [],
        }

        let request = editSession
          ? axios.put('/api/pipeline/instructor/session', body)
          : axios.post('/api/pipeline/instructor/session', body)

        await request
          .then(pluckData)
          .then(() => dispatch(fetchAllInstructorSessions()))
          .then(() => {
            showSuccess(
              editSession
                ? 'Session updated successfully'
                : 'Session created successfully',
            )
            form.reset()
            onClose()
          })
      } catch (error: any) {
        showError(
          error?.response?.data ||
            `Failed to ${editSession ? 'update' : 'create'} session`,
        )
      }
    },
  })

  useEffect(() => {
    if (open) {
      if (editSession) {
        form.setFieldValue('eventID', editSession.eventID)
        form.setFieldValue('name', editSession.name || '')
        form.setFieldValue(
          'sessionTime',
          dayjs
            .utc(editSession.sessionTime)
            .tz(editSession.timeZone)
            .format('YYYY-MM-DD HH:mm:ss'),
        )
        form.setFieldValue(
          'endTime',
          editSession.endTime
            ? dayjs
                .utc(editSession.endTime)
                .tz(editSession.timeZone)
                .format('YYYY-MM-DD HH:mm:ss')
            : '',
        )
        form.setFieldValue('timeZone', editSession.timeZone)
        form.setFieldValue('instructorIDs', editSession.instructorIDs)
      } else if (initialDate) {
        form.setFieldValue(
          'sessionTime',
          initialDate
            .hour(10)
            .minute(0)
            .second(0)
            .format('YYYY-MM-DD HH:mm:ss'),
        )
        if (instructorID) {
          form.setFieldValue('instructorIDs', [instructorID])
        }
      }
    }
  }, [open, initialDate, editSession, form, instructorID])

  let handleClose = () => {
    form.reset()
    setCourseFilter('their')
    onClose()
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        aria-labelledby="session-form-title"
        aria-describedby="session-form-description"
        slotProps={{
          paper: modalPaperProps,
        }}
        maxWidth="sm"
      >
        <Paper sx={{ px: 2, py: 1 }}>
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              form.handleSubmit()
            }}
          >
            <DialogTitle id="session-form-title" sx={{ textAlign: 'center' }}>
              {editSession ? 'Edit Session' : 'Create New Session'}
            </DialogTitle>
            <DialogContent
              id="session-form-description"
              sx={{
                px: 1,
                pb: 3,
                display: 'flex',
                gap: 2,
                flexDirection: 'column',
              }}
            >
              <Box component="fieldset" sx={{ border: 'none', p: 0, m: 0 }}>
                <Box
                  component="legend"
                  sx={{
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    overflow: 'hidden',
                    clip: 'rect(0, 0, 0, 0)',
                  }}
                >
                  Session Details
                </Box>
                <form.Subscribe
                  selector={(state) =>
                    [state.values.instructorIDs, state.values.eventID] as [
                      string[],
                      string,
                    ]
                  }
                >
                  {([instructorIDs, eventID]) => {
                    let teachCounts = getEventTeachCounts(instructorIDs)
                    let filteredEvents =
                      courseFilter === 'their'
                        ? events.filter((event) => teachCounts.has(event.id))
                        : events
                    // Keep the currently-selected course in the options list even
                    // if it doesn't match the active filter — MUI's Autocomplete
                    // warns (and behaves inconsistently) when a controlled value
                    // isn't present in `options`, which otherwise happens the
                    // moment someone toggles filters after picking a course.
                    let selectedEvent = events.find((e) => e.id === eventID)
                    let optionsWithSelection =
                      selectedEvent &&
                      !filteredEvents.some((e) => e.id === selectedEvent.id)
                        ? [...filteredEvents, selectedEvent]
                        : filteredEvents
                    let sortedEvents = sortBy(
                      (event) => -(teachCounts.get(event.id) ?? 0),
                      optionsWithSelection,
                    )

                    return (
                      <Box>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            mb: 1,
                          }}
                        >
                          <ToggleButtonGroup
                            size="small"
                            value={courseFilter}
                            exclusive
                            onChange={(_, value) =>
                              value && setCourseFilter(value)
                            }
                          >
                            <ToggleButton value="their">
                              Their courses
                            </ToggleButton>
                            <ToggleButton value="all">All courses</ToggleButton>
                          </ToggleButtonGroup>
                        </Box>
                        <form.Field name="eventID">
                          {(field) => (
                            <Autocomplete
                              fullWidth
                              autoFocus
                              options={sortedEvents}
                              getOptionLabel={(event) => event.name}
                              value={
                                events.find(
                                  (e) => e.id === field.state.value,
                                ) || null
                              }
                              onChange={(_, newValue) => {
                                field.handleChange(newValue?.id || '')
                              }}
                              slotProps={{
                                paper: {
                                  sx: {
                                    bgcolor: 'background.paperDark',
                                  },
                                },
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  id="session-event-field"
                                  label="Course / class"
                                  error={shouldShowZodFieldError(
                                    field.state.meta,
                                  )}
                                  helperText={
                                    shouldShowZodFieldError(field.state.meta)
                                      ? getZodValidationError(
                                          field.state.meta.errors,
                                        )
                                      : ''
                                  }
                                  required
                                  aria-required="true"
                                  sx={modalInputSx}
                                />
                              )}
                              renderOption={(props, event) => {
                                let teachCount = teachCounts.get(event.id) ?? 0
                                return (
                                  <Box
                                    key={event.id}
                                    component="li"
                                    onClick={props.onClick}
                                    onMouseDown={props.onMouseDown}
                                    onTouchStart={props.onTouchStart}
                                    role={props.role}
                                    aria-selected={props['aria-selected']}
                                    data-option-index={
                                      (props as any)['data-option-index']
                                    }
                                    sx={{
                                      display: 'flex',
                                      gap: 2,
                                      alignItems: 'center',
                                      px: 2,
                                      py: 1.25,
                                      borderBottom: 1,
                                      borderColor: 'divider',
                                    }}
                                  >
                                    {event.thumbnail ? (
                                      <Box
                                        component="img"
                                        src={`${import.meta.env.VITE_CLOUDFRONT_URL}/images/${event.thumbnail.replace('originals', 'resized')}`}
                                        alt={event.name}
                                        sx={{
                                          width: 48,
                                          height: 48,
                                          borderRadius: 1,
                                          objectFit: 'cover',
                                          flexShrink: 0,
                                        }}
                                      />
                                    ) : (
                                      <Box
                                        sx={{
                                          width: 48,
                                          height: 48,
                                          borderRadius: 1,
                                          bgcolor: 'background.paperDark',
                                          flexShrink: 0,
                                        }}
                                      />
                                    )}
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                      <Typography variant="body2" noWrap>
                                        {event.name}
                                      </Typography>
                                      {(event.hours || teachCount > 0) && (
                                        <Typography
                                          color="text.secondary"
                                          variant="caption"
                                        >
                                          {event.hours &&
                                            `${event.hours} hour${event.hours !== 1 ? 's' : ''}`}
                                          {event.hours &&
                                            teachCount > 0 &&
                                            ' • '}
                                          {teachCount > 0 &&
                                            `Taught ${teachCount} time${teachCount !== 1 ? 's' : ''}`}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                )
                              }}
                              isOptionEqualToValue={(option, value) =>
                                option.id === value?.id
                              }
                              noOptionsText="No events available"
                            />
                          )}
                        </form.Field>
                      </Box>
                    )
                  }}
                </form.Subscribe>

                <form.Field name="name">
                  {(field) => (
                    <TextField
                      fullWidth
                      id="session-name-field"
                      label="Session Name (optional)"
                      name="name"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      sx={{ bgcolor: 'background.paperDark', mt: 2 }}
                    />
                  )}
                </form.Field>

                <form.Field name="timeZone">
                  {(field) => (
                    <Box sx={{ mt: 2 }}>
                      <TimeZoneDropdown
                        value={field.state.value}
                        onChange={(tz: string) => field.handleChange(tz)}
                        variant="outlined"
                      />
                    </Box>
                  )}
                </form.Field>

                {isComplianceAdmin && (
                  <form.Field name="instructorIDs">
                    {(field) => (
                      <Box sx={{ mt: 2 }}>
                        <Autocomplete
                          multiple
                          options={allInstructors}
                          getOptionLabel={(i) =>
                            `${i.name}${i.credentials ? ` (${i.credentials})` : ''}`
                          }
                          value={allInstructors.filter((i) =>
                            field.state.value.includes(i.id),
                          )}
                          onChange={(_, newValue) =>
                            field.handleChange(newValue.map((i) => i.id))
                          }
                          slotProps={{
                            paper: { sx: { bgcolor: 'background.paperDark' } },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Instructors"
                              error={shouldShowZodFieldError(field.state.meta)}
                              helperText={
                                shouldShowZodFieldError(field.state.meta)
                                  ? getZodValidationError(
                                      field.state.meta.errors,
                                    )
                                  : ''
                              }
                              sx={modalInputSx}
                            />
                          )}
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                        />
                      </Box>
                    )}
                  </form.Field>
                )}

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <form.Field name="sessionTime">
                    {(field) => (
                      <Box sx={{ mt: 2 }}>
                        <DateTimePicker
                          label="Start Time"
                          value={
                            field.state.value ? dayjs(field.state.value) : null
                          }
                          onChange={(newValue) =>
                            field.handleChange(
                              newValue?.format('YYYY-MM-DD HH:mm:ss') || '',
                            )
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              id: 'session-time-field',
                              error: shouldShowZodFieldError(field.state.meta),
                              helperText: shouldShowZodFieldError(
                                field.state.meta,
                              )
                                ? getZodValidationError(field.state.meta.errors)
                                : '',
                              required: true,
                              'aria-required': 'true',
                              sx: modalInputSx,
                            },
                          }}
                        />
                      </Box>
                    )}
                  </form.Field>

                  <form.Field name="endTime">
                    {(field) => (
                      <Box sx={{ mt: 2 }}>
                        <DateTimePicker
                          label="End Time (optional)"
                          value={
                            field.state.value ? dayjs(field.state.value) : null
                          }
                          onChange={(newValue) =>
                            field.handleChange(
                              newValue?.format('YYYY-MM-DD HH:mm:ss') || '',
                            )
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              id: 'session-end-time-field',
                              error: shouldShowZodFieldError(field.state.meta),
                              helperText: shouldShowZodFieldError(
                                field.state.meta,
                              )
                                ? getZodValidationError(field.state.meta.errors)
                                : '',
                              sx: modalInputSx,
                            },
                          }}
                        />
                      </Box>
                    )}
                  </form.Field>
                </LocalizationProvider>
              </Box>
            </DialogContent>
            <DialogActions
              disableSpacing={true}
              sx={{ flexDirection: 'column', gap: 1, pt: 0, pb: 3, mt: 0 }}
            >
              <form.Subscribe
                selector={(state) => [state.isSubmitting]}
                children={([isSubmitting]) => (
                  <>
                    <Button
                      color="primary"
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={isSubmitting}
                    >
                      {editSession ? 'Update Session' : 'Create Session'}
                    </Button>
                    <Button
                      color="error"
                      onClick={handleClose}
                      variant="contained"
                      fullWidth
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    {editSession && onDelete && (
                      <Button
                        type="button"
                        color="error"
                        onClick={() => {
                          form.reset()
                          setCourseFilter('their')
                          onDelete(editSession.id)
                        }}
                        variant="outlined"
                        fullWidth
                        disabled={isSubmitting}
                        startIcon={<DeleteIcon />}
                      >
                        Delete Session
                      </Button>
                    )}
                  </>
                )}
              />
            </DialogActions>
          </form>
        </Paper>
      </Dialog>
      {NotificationComponent}
    </>
  )
}
